import * as React from "react";
import { useSelector } from "react-redux";
import { Button, Typography, Stack, Box, Grid, Select, MenuItem, Dialog, DialogContent, useMediaQuery, Switch, FormControlLabel, TextField, Stepper, Step, StepLabel } from "@mui/material";
import { ModeEdit as ModeEditIcon, Help as HelpIcon, DateRange as DateRangeIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import BalanceDetailsIcon from "../../../icons/RecurringDetailsIcon.svg";
import CloseModalIcon from "../../../icons/RoundCloseIcon.svg";
import { AutoPayFrequency, AutoPayConfigDtoTypeEnum } from "@inosio/payments-sdk";
import { CalendarPicker, LocalizationProvider, StaticDatePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MaskedInputState, StyledMaskedInput } from "../../../shared/MaskedInput";
import { addMonths } from "date-fns/esm";
import { selectBalanceDetails, selectPaymentConfigs } from "../sagas/residentpaymentsSlice";
import NumberFormat from "react-number-format";
import { getSumOfPropPayTypesAmount } from "./UseSharedMethods";
import { format, getMonth, setMonth, getYear, setYear } from "date-fns";

const useStyles = makeStyles({
  editBtn: {
    background: "#39A2DB",
    borderRadius: "50%",
    width: "25px",
    height: "25px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "5%",
  },
});

export interface RecurringModalContentsReturnType {
  Frequency: AutoPayFrequency;
  FixedAmount?: number;
  StartDate: string;
  EndDate?: string;
  Type: AutoPayType;
  VariableMaxAmountAllowed?: number;
}

interface RecurringModalContentsType {
  isEditingMode: boolean;
  maskedInputValues: MaskedInputState;
  setOpenBalanceDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
  closeCallback: () => void;
  setRecurrintStartDayNum: React.Dispatch<React.SetStateAction<number>>;
  continueCallback: (returnObj: RecurringModalContentsReturnType) => void;
}

enum AutoPayType {
  Fixed = "Fixed",
  Variable = "Variable",
}

export default function RecurringModalContents(props: RecurringModalContentsType) {
  const { isEditingMode, maskedInputValues, setOpenBalanceDetailsModal, closeCallback, continueCallback, setRecurrintStartDayNum } = props;
  const { editBtn } = useStyles();
  const currentBalanceInfo = useSelector(selectBalanceDetails);
  const paymentConfig = useSelector(selectPaymentConfigs);
  const autoPayConfigs = paymentConfig.AutoPayConfigs;

  const fixedAmountCents = getSumOfPropPayTypesAmount(maskedInputValues);
  const vapConfig = autoPayConfigs.find((ap) => ap.Type === AutoPayConfigDtoTypeEnum.Vap);
  const fapConfig = autoPayConfigs.find((ap) => ap.Type === AutoPayConfigDtoTypeEnum.Fap);
  const isVapEnabled = vapConfig?.Enabled;
  const isFapEnabled = fapConfig?.Enabled;

  const [expanded, setExpanded] = React.useState<string | false>("amountType");
  const isOnMobileScreen = useMediaQuery("(max-width:600px)");
  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  const style = {
    height: isOnMobileScreen ? "" : "660px",
  };

  // disabled anyday outside of config autopay days
  const paymentClassMap: { FAP: number[]; VAP: number[] } = {
    FAP: [fapConfig?.NotBeforeDay ?? 1, fapConfig?.NotAfterDay ?? 1],
    VAP: [vapConfig?.NotBeforeDay ?? 1, vapConfig?.NotAfterDay ?? 1],
  };

  const shouldDisableDate = (calendarDay: Date) => {
    // disable any day before today
    const today = new Date();
    const calendarDayNumber = calendarDay.getDate();
    let notBeforeDay: number = 0;
    let notAfterDay: number = 32;

    // 0 and 32 so the check fails when selectedAutopayType has not been set
    switch (selectedAutopayType) {
      case AutoPayType.Variable:
        notBeforeDay = paymentClassMap.VAP?.[0] ?? 0;
        notAfterDay = paymentClassMap.VAP?.[1] ?? 32;
        break;
      case AutoPayType.Fixed:
        notBeforeDay = paymentClassMap.FAP?.[0] ?? 0;
        notAfterDay = paymentClassMap.FAP?.[1] ?? 32;
        break;
      default:
        break;
    }
    const isDayInPast = calendarDay < today;

    //this tells us how to view the winda.
    //if NBF is smaller (ie, [1, 5]), then we block any day less than NBF OR greater than NAF
    //IF NBF is not smaller (ie, [25, 10]), then we block anyhting less than NAF AND greater than NBF

    if (notBeforeDay === notAfterDay) {
      return calendarDayNumber !== notBeforeDay;
    }

    const isNbfIsSmaller = notBeforeDay < notAfterDay;
    const result = isDayInPast || (isNbfIsSmaller && (calendarDayNumber < notBeforeDay || calendarDayNumber > notAfterDay)) || (!isNbfIsSmaller && calendarDayNumber > notAfterDay && calendarDayNumber < notBeforeDay);

    return result;
  };
  const [selectedAutopayType, setSelectedAutopayType] = React.useState<AutoPayType|null>(
    ()=>{
       return (isVapEnabled && !isFapEnabled)? AutoPayType.Variable: (!isVapEnabled && isFapEnabled)? AutoPayType.Fixed: null});
  const [frequency, setFrequency] = React.useState(AutoPayFrequency.M);

  const [isMaxAmountOn, handleIsMaxAmountOn] = React.useState(false);
  const handleChangeMaxAmountSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleIsMaxAmountOn(event.target.checked);

    if (!event.target.checked) {
      setVapUpperLimitValues({ amountOwed: "0", securityDeposit: "0", misc: "0" });
    }
  };

  //This is so weird, why do we use this MaskedInput instead of simple one? Perhaps we value the UI component...
  const [vapUpperLimitValues, setVapUpperLimitValues] = React.useState<MaskedInputState>({ amountOwed: "0", securityDeposit: "0", misc: "0" });
  const handleVapUpperLimitValuesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVapUpperLimitValues({
      ...vapUpperLimitValues,
      [event.target.name]: event.target.value,
    });
  };

  const disableContinueButton = () => {
    return ![firstPaymentDate, selectedAutopayType, frequency].every((field) => {
      return !!field;
    });
  };

  const disableFirstPayAccordion = () => !selectedAutopayType;
  const [firstPaymentDate, setFirstPaymentDate] = React.useState<Date | null>(null);
  const [openFirstDateModal, setOpenFirstDateModal] = React.useState(false);

  const [lastPaymentDate, setLastPaymentDate] = React.useState<Date | null>(null);
  const [openLastDateModal, setOpenLastDateModal] = React.useState(false);
  const disableLastPayAccordion = () => {
    return ![firstPaymentDate, selectedAutopayType].every((field) => {
      return !!field;
    });
  };

  const handleContinue = () => {
    const returnObj: RecurringModalContentsReturnType = {
      Frequency: frequency,
      FixedAmount: selectedAutopayType === AutoPayType.Fixed ? fixedAmountCents: undefined,
      StartDate: format(new Date(firstPaymentDate ?? ''), "yyyy-MM-dd"),
      EndDate: lastPaymentDate ? format(new Date(lastPaymentDate ?? ''), "yyyy-MM-dd"): undefined,
      Type: selectedAutopayType ?? AutoPayType.Variable,
      VariableMaxAmountAllowed: selectedAutopayType === AutoPayType.Variable ? (getSumOfPropPayTypesAmount(vapUpperLimitValues) ?? undefined): undefined,
    };
    continueCallback(returnObj);
  }  

  return (
    <Box sx={{ width: "100%" }}>
      <div>
        <Stepper activeStep={0}>
          {[1, 2].map((step, index) => {
            return (
              <Step key={index}>
                <StepLabel></StepLabel>
              </Step>
            );
          })}
        </Stepper>
        
        <Grid container justifyContent="center" alignItems="flex-start" sx={{ marginTop: "2%" }}>
          <Grid item xs={12} sx={{ marginTop: "3%" }} style={{ borderBottom: "1px solid #F1F0F5" }}>
            <Typography
              style={{
                color: "#14142B",
                fontSize: "16px",
                lineHeight: "22px",
                fontFamily: "'Work Sans', sans-serif",
                fontWeight: 400,
                textAlign: "center",
                marginBottom: "25px",
              }}
            >
              <NumberFormat
                thousandsGroupStyle="thousand"
                value={(currentBalanceInfo.Balance ?? 0) / 100}
                prefix="Current Balance: $"
                decimalSeparator="."
                displayType="text"
                type="text"
                thousandSeparator={true}
                allowNegative={true}
                decimalScale={2}
                fixedDecimalScale={true}
              />
              <img src={BalanceDetailsIcon} alt="Show Balance Detail" style={{ cursor: "pointer", marginLeft: '5px' }} onClick={() => setOpenBalanceDetailsModal(true)} />
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "3%" }}>
            {autoPayConfigs && (
              <Accordion
                expanded={expanded === "amountType"}
                onChange={handleChange("amountType")}
                sx={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  borderBottom: "2px solid #F1F0F5",
                }}
                id="apAmount"
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
                  <Typography
                    id="recurring-pay-amount"
                    style={{
                      color: "#14142B",
                      fontSize: "18px",
                      lineHeight: "22px",
                      fontWeight: 700,
                      fontFamily: "'Open Sans', sans-serif",
                      marginLeft: "-15px",
                      marginBottom: "2%",
                    }}
                  >
                    Recurring Payment Amount
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Select
                    onChange={(e: any) => setSelectedAutopayType(e.target.value)}
                    value={selectedAutopayType || ""}
                    displayEmpty
                    inputProps={{
                      "aria-label": "Without label",
                      classes: {
                        icon: {
                          fill: "white",
                        },
                        root: {
                          color: "white",
                        },
                      },
                    }}
                    style={{
                      borderRadius: "24px",
                      height: "33px",
                      background: selectedAutopayType ? "#39A2DB" : "#F1F0F5",
                      border: "none",
                      outline: "none",
                      color: selectedAutopayType ? "#fff" : "#000",
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "24px",
                          background: "#fff",
                        },
                      },
                    }}
                    data-select="apAmount"
                    id="select-amount"
                  >
                    {isFapEnabled && isVapEnabled && (
                      <MenuItem value="" disabled style={{ background: "#fff" }}>
                        Select Amount{" "}
                      </MenuItem>
                    )}
                    {isVapEnabled && (
                      <MenuItem value="Variable" style={{ background: "#fff" }} id="vap">
                        My Full Balance
                      </MenuItem>
                    )}
                    {isFapEnabled && (
                      <MenuItem value="Fixed" style={{ background: "#fff" }} id="fap">
                        Fixed Amount
                      </MenuItem>
                    )}
                  </Select>
                  {selectedAutopayType === AutoPayType.Variable && vapConfig?.AutopayAllowToSetMaxAmount && (
                    <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
                      <FormControlLabel control={<Switch checked={isMaxAmountOn} onChange={handleChangeMaxAmountSwitch} inputProps={{ "aria-label": "upper limit" }} id="check-box" />} label="set upper limit" />
                      <StyledMaskedInput disabled={!isMaxAmountOn} onBlur={(e: any) => {}} label="" name="amountOwed" values={vapUpperLimitValues} handleChange={handleVapUpperLimitValuesChange} id="dollar" />
                    </Stack>
                  )}
                  <Grid item sx={{ marginTop: "10px", marginLeft: "-70px" }} xs={12} md={7}>
                    {selectedAutopayType === AutoPayType.Fixed && (
                      <Grid container sx={{ cursor: "pointer" }} justifyContent={"center"} alignItems={"center"}>
                        <Grid item xs={4}>
                          <Typography
                            id="payment-modal-sub-amount"
                            style={{
                              background: "#F1F0F5",
                              borderRadius: "24px",
                              textAlign: "center",
                            }}
                          >
                            <NumberFormat
                              thousandsGroupStyle="thousand"
                              value={fixedAmountCents / 100}
                              prefix="$"
                              decimalSeparator="."
                              displayType="text"
                              type="text"
                              thousandSeparator={true}
                              allowNegative={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                          </Typography>
                        </Grid>
                        <Grid item className={editBtn} style={{ marginLeft: "-5px" }}>
                          <ModeEditIcon
                            sx={{ color: "#fff" }}
                            onClick={() => {
                              setOpenBalanceDetailsModal(true);
                            }}
                            id="modal-icon"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "3%" }}>
            <Accordion
              expanded={expanded === "firstPayment"}
              onChange={handleChange("firstPayment")}
              sx={{
                border: "none",
                outline: "none",
                boxShadow: "none",
                borderBottom: "2px solid #F1F0F5",
                backgroundColor: "#ffffff !important",
              }}
              id="firstPayDate"
              disabled={disableFirstPayAccordion()}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
                <Typography
                  id="first-payment"
                  style={{
                    color: "#14142B",
                    fontSize: "18px",
                    lineHeight: "22px",
                    fontWeight: 700,
                    fontFamily: "'Open Sans', sans-serif",
                    marginLeft: "-15px",
                    marginBottom: "2%",
                  }}
                >
                  First Payment{" "}
                  <HelpIcon
                    sx={{
                      fontSize: 18,
                      color: "#C4C4C4",
                      cursor: "pointer",
                    }}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" justifyContent="flex-start" alignItems="flex-start">
                  <Button
                    style={{
                      width: firstPaymentDate ? "120px" : "53px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      textTransform: "capitalize",
                      background: !firstPaymentDate ? "#F1F0F5" : "#39A2DB",
                      color: !firstPaymentDate ? "#000" : "#fff",
                      fontSize: "18px",
                      height: "33px",
                      textAlign: "center",
                      padding: "5px",
                    }}
                    id="firstPayDateBtn"
                    onClick={() => setOpenFirstDateModal(true)}
                  >
                    {!firstPaymentDate && !isEditingMode ? (
                      <DateRangeIcon
                        sx={{
                          fontSize: 20,
                          color: "#000",
                          cursor: "pointer",
                          marginTop: "1px",
                        }}
                      />
                    ) : (
                      <span style={{ cursor: "pointer", fontSize: "16px" }}>
                        {format(new Date(firstPaymentDate ?? ''), "MM/dd/yyyy")}
                      </span>
                    )}
                  </Button>
                  <Dialog
                    open={openFirstDateModal}
                    onClose={() => setOpenFirstDateModal(false)}
                    fullScreen={isOnMobileScreen}
                    fullWidth={true}
                    disableEnforceFocus
                    hideBackdrop={true}
                    PaperProps={{
                      style: {
                        pointerEvents: "auto",
                        borderRadius: "24px",
                        height: isOnMobileScreen ? "" : "700px",
                      },
                    }}
                    id="firstPayCalandarDialog"
                  >
                    <DialogContent>
                      <Box sx={style}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                          <Typography id="payment-method-title">First Payment</Typography>
                          <div>
                            <img src={CloseModalIcon} alt="close" style={{ cursor: "pointer" }} onClick={() => setOpenFirstDateModal(false)} />
                          </div>
                        </Stack>
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ marginTop: "20%" }}>
                          {openFirstDateModal ? (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <CalendarPicker
                                onChange={(e) => {
                                  setFirstPaymentDate(e);
                                  setRecurrintStartDayNum(e?.getDate()||0);
                                  setOpenFirstDateModal(false);
                                }}
                                minDate={new Date()}
                                date={firstPaymentDate}
                                disableHighlightToday
                                shouldDisableDate={shouldDisableDate}
                              />
                            </LocalizationProvider>
                          ) : (
                            <></>
                          )}
                        </Stack>
                      </Box>
                    </DialogContent>
                  </Dialog>
                  <Select
                    onChange={(e) => setFrequency(e.target.value as AutoPayFrequency)}
                    value={frequency}
                    displayEmpty
                    inputProps={{ "aria-label": "Without label" }}
                    style={{
                      borderRadius: "24px",
                      height: "33px",
                      width: "171px",
                      background: !frequency ? "#F1F0F5" : "#39A2DB",
                      border: "none",
                      outline: "none",
                      color: !frequency ? "#000" : "#fff",
                      boxShadow: "none",
                      textAlign: "center",
                      marginLeft: "10px",
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: "24px",
                          background: "#fff",
                        },
                      },
                    }}
                    id="select-frequency"
                  >
                    <MenuItem disabled value="" style={{ background: "#fff" }}>
                      Select Frequency{" "}
                    </MenuItem>
                    <MenuItem value={AutoPayFrequency.M} style={{ background: "#fff" }} id="month-wise">
                      Monthly
                    </MenuItem>
                    <MenuItem value={AutoPayFrequency.Q} style={{ background: "#fff" }}>
                      Quarterly
                    </MenuItem>
                    <MenuItem value={AutoPayFrequency.S} style={{ background: "#fff" }}>
                      Semi-annually
                    </MenuItem>
                    <MenuItem value={AutoPayFrequency.A} style={{ background: "#fff" }}>
                      Annually
                    </MenuItem>
                  </Select>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "3%" }}>
            <Accordion
              expanded={expanded === "lastPayment"}
              onChange={handleChange("lastPayment")}
              sx={{
                border: "none",
                outline: "none",
                boxShadow: "none",
                borderBottom: "2px solid #F1F0F5",
                backgroundColor: "#ffffff !important",
              }}
              id="lastPayDate"
              disabled={disableLastPayAccordion()}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
                <Typography
                  style={{
                    color: "#14142B",
                    fontSize: "18px",
                    lineHeight: "22px",
                    fontWeight: 700,
                    fontFamily: "'Open Sans', sans-serif",
                    marginLeft: "-15px",
                    marginBottom: "2%",
                  }}
                >
                  Last Payment{" "}
                  <HelpIcon
                    sx={{
                      fontSize: 18,
                      color: "#C4C4C4",
                      cursor: "pointer",
                    }}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
                  <Button
                    style={{
                      width: !lastPaymentDate ? "53px" : "120px",
                      height: "33px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      textTransform: "capitalize",
                      background: !lastPaymentDate ? "#F1F0F5" : "#39A2DB",
                      color: !lastPaymentDate ? "#000" : "#fff",
                      fontSize: "18px",
                      marginLeft: "10px",
                      textAlign: "center",
                      padding: "5px",
                    }}
                    onClick={() => {
                      setOpenLastDateModal(true);
                    }}
                  >
                    {!lastPaymentDate ? (
                      <DateRangeIcon sx={{ fontSize: 20, color: "#000", cursor: "pointer", marginTop: "1px" }} />
                    ) : (
                      <span
                        style={{
                          cursor: "pointer",
                          padding: " 5px 10px",
                          height: "30px",
                          fontSize: "16px",
                          marginBottom: "4px",
                        }}
                      >
                        {format(new Date(lastPaymentDate ?? ''), "MM/yyyy")}
                      </span>
                    )}
                  </Button>
                  <Dialog
                    open={openLastDateModal}
                    onClose={() => setOpenLastDateModal(false)}
                    fullScreen={isOnMobileScreen}
                    fullWidth={true}
                    disableEnforceFocus
                    hideBackdrop={true}
                    PaperProps={{
                      style: {
                        pointerEvents: "auto",
                        borderRadius: "24px",
                        height: isOnMobileScreen ? "" : "700px",
                      },
                    }}
                  >
                    <DialogContent>
                      <Box sx={style}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                          <Typography id="payment-method-title">Last Payment</Typography>
                          <div>
                            <img src={CloseModalIcon} alt="close" style={{ cursor: "pointer" }} onClick={() => setOpenLastDateModal(false)} />
                          </div>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                          spacing={2}
                          sx={{
                            marginTop: "20%",
                            ".MuiTypography-root.Mui-selected": {
                              backgroundColor: "#fff",
                              color: "#000",
                            },
                            ".MuiTypography-root, .PrivatePickersYear-root>button": {
                              borderRadius: 0,
                              height: "70px",
                              border: "0.5px solid #D3D3D3",
                              fontFamily: "Work Sans",
                              fontSize: "12px",
                              fontWeight: 600,
                              m: 0,
                            },
                            ".MuiCalendarPicker-viewTransitionContainer ": {
                              mt: 3,
                            },
                          }}
                        >
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <StaticDatePicker
                              displayStaticWrapperAs="desktop"
                              openTo="month"
                              value={lastPaymentDate || addMonths(firstPaymentDate ?? new Date(), 12)}
                              onChange={(e) => {
                                // do nothing
                              }}
                              onMonthChange={(e) => {
                                const year = getYear(e ?? new Date());
                                const month = getMonth(e ?? new Date());
                                let lastDate = setYear(firstPaymentDate ?? new Date(), year);
                                lastDate = setMonth(lastDate, month);
                                setLastPaymentDate(lastDate);
                                setOpenLastDateModal(false);
                              }}
                              defaultCalendarMonth={addMonths(firstPaymentDate ?? new Date(), 12)}
                              renderInput={(params) => <TextField {...params} />}
                              views={["year", "month"]}
                              minDate={firstPaymentDate}
                            />
                          </LocalizationProvider>
                        </Stack>
                        <Grid container mt={4} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                          <Grid item xs={8}>
                            <Typography sx={{ fontFamily: "Work Sans", fontSize: "12px", fontWeight: 400 }}>*If your debit day is 28 - 31 and this day is not in the current month, we will execute the payment on the last day of the month.</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </DialogContent>
                  </Dialog>
                  <Typography>Or</Typography>
                  <Button
                    style={{
                      width: "125px",
                      padding: " 5px 10px",
                      fontFamily: "'Work Sans', sans-serif",
                      borderRadius: "24px",
                      border: "none",
                      background: !lastPaymentDate ? "#39A2DB" : "#F1F0F5",
                      textTransform: "capitalize",
                      fontSize: "16px",
                      lineHeight: "22px",
                      color: !lastPaymentDate ? "#fff" : "#000",
                    }}
                    size="small"
                    onClick={() => setLastPaymentDate(null)}
                    id="lastPayIndefiniteBtn"
                  >
                    Indefinite{" "}
                    <HelpIcon
                      sx={{
                        fontSize: 18,
                        color: "#C4C4C4",
                        cursor: "pointer",
                      }}
                    />
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "63px" }}>
            <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" spacing={2} style={{ width: "100%" }}>
              <Button
                style={{
                  width: "45px",
                  fontFamily: "'Work Sans', sans-serif",
                  borderRadius: "24px",
                  height: "33px",
                  textTransform: "capitalize",
                  fontSize: "14px",
                  color: "#14142B",
                }}
                size="small"
                onClick={() => closeCallback()}
              >
                Cancel
              </Button>
              <Button
                disabled={disableContinueButton()}
                style={{
                  width: "99px",
                  fontFamily: "'Work Sans', sans-serif",
                  borderRadius: "24px",
                  backgroundColor: disableContinueButton() ? "#F1F0F5" : "#39A2DB",
                  height: "33px",
                  textTransform: "capitalize",
                  fontSize: "14px",
                  color: "#fff",
                }}
                size="small"
                onClick={() => {
                  // setDisableRecurringContinue(true); // make it compulsory for user to select a card at next step
                  handleContinue();
                }}
                id="continueApBtn"
              >
                Continue
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </div>
    </Box>
  );
}
